/**
 * shared/session-death-codes.ts
 *
 * The one place the server and the client agree on how "the session you were
 * using is GONE" is spelled on the wire.
 *
 * WHY SHARED RATHER THAN A CONSTANT ON EITHER SIDE. These codes are values BOTH
 * halves must match on, and the consequence of a mismatch is silent and
 * security-relevant. `server/middleware/zero-trust/gates.ts` EMITS them from the
 * branches that actually call `req.session.destroy()`; `client/src/lib/
 * queryClient.ts` MATCHES on them to decide that the tab's in-memory vault KEK
 * has outlived its session and must be wiped (AC-48 / L0-9 / Rule 3).
 *
 * Two independent string literals is the shape where one side gets renamed and
 * the other quietly stops matching — and the failure mode here is not a broken
 * screen but a live 32-byte KEK sitting in tab memory after the server has
 * destroyed the session it belonged to, with nothing failing to say so. The
 * companion guard test (`tests/guards/session-death-codes.test.ts`) reads the
 * gates.ts source and asserts the two sets still coincide, so adding a fourth
 * destroy branch without adding its code here fails the build.
 *
 * Follows the `shared/acting-context.ts` precedent (#2459), which shares a wire
 * code across the same three consumers for the same reason.
 *
 * NOT a list of "auth failures" generally. `INSUFFICIENT_AAL` and
 * `INSUFFICIENT_ROLE` are refusals by a session that is still ALIVE — wiping the
 * KEK on those would destroy usable key material on an ordinary step-up prompt.
 */

/** No usable session on the request (never established, or just destroyed). */
export const UNAUTHENTICATED = "UNAUTHENTICATED" as const;

/** 10-minute inactivity timeout tripped — session destroyed (Session Policy;
 *  owner directive 2026-08-24, superseding the 15-minute window). */
export const SESSION_EXPIRED_INACTIVE = "SESSION_EXPIRED_INACTIVE" as const;

/** 30-minute absolute lifetime reached — session destroyed (Session Policy;
 *  owner directive 2026-08-31, superseding the 12 h cap). */
export const SESSION_EXPIRED_ABSOLUTE = "SESSION_EXPIRED_ABSOLUTE" as const;

/**
 * Every code emitted from a branch that destroys the session server-side.
 *
 * `UNAUTHENTICATED` is the one a Forensic Pause lift produces: the lift
 * transaction bumps `profiles.sessionEpoch` and deletes every session row, so
 * the next request from any surviving tab fails the epoch check and is
 * destroyed. It is also emitted when no session existed in the first place —
 * harmless for the KEK wipe, which is idempotent and a no-op on an empty slot.
 */
export const SESSION_DEATH_CODES = [
  UNAUTHENTICATED,
  SESSION_EXPIRED_INACTIVE,
  SESSION_EXPIRED_ABSOLUTE,
] as const;

export type SessionDeathCode = (typeof SESSION_DEATH_CODES)[number];

/** Whether a server error code means the session behind this request is gone. */
export function isSessionDeathCode(code: string | undefined): code is SessionDeathCode {
  return code !== undefined && (SESSION_DEATH_CODES as readonly string[]).includes(code);
}
